import React from 'react';
import { FrequencyOption, StressLevel, ExerciseType } from '@/context/UserDataContext';
import { RadioOption } from '../RadioButtonGroup';
import { Armchair, Beef, Beer, Bike, Brain, CupSoda, Dumbbell, Fish, FishOff, Footprints, Frown, GlassWater, Laugh, Leaf, Martini, Meh, Milk, MilkOff, Siren, Smile, Volleyball, Wine } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AnimatedSelectionOption } from '../AnimatedSelectionGroup';

// Update the RadioOption type to use LucideIcon
export type RadioOptionWithIcon<T extends string> = {
  value: T;
  label: string;
  icon: LucideIcon;
  emojiCount?: number;
};

// Create a function that returns the options to avoid JSX evaluation during module initialization
export const getMeatFrequencyOptions = (): AnimatedSelectionOption<FrequencyOption>[] => [
  { value: 'never', label: 'Never', icon: <Leaf className="h-5 w-5" /> },
  { value: 'once_a_week', label: 'Once a week', icon: <Beef className="h-5 w-5" /> },
  { value: 'two_three_times_a_week', label: '2 - 3 times a week', icon: <Beef className="h-5 w-5" /> },
  { value: 'four_plus_times_a_week', label: '4+ times a week', icon: <Beef className="h-5 w-5" /> }
];

export const getSeafoodFrequencyOptions = (): AnimatedSelectionOption<FrequencyOption>[] => [
  { value: 'never', label: 'Never', icon: <FishOff className="h-5 w-5" /> },
  { value: 'once_a_week', label: 'Once a week', icon: <Fish className="h-5 w-5" /> },
  { value: 'two_three_times_a_week', label: '2 - 3 times a week', icon: <Fish className="h-5 w-5" /> },
  { value: 'four_plus_times_a_week', label: '4+ times a week', icon: <Fish className="h-5 w-5" /> }
];

export const getDairyFrequencyOptions = (): AnimatedSelectionOption<FrequencyOption>[] => [
  { value: 'never', label: 'Never', icon: <MilkOff className="h-5 w-5" /> },
  { value: 'once_a_week', label: 'Once a week', icon: <Milk className="h-5 w-5" /> },
  { value: 'two_three_times_a_week', label: '2 - 3 times a week', icon: <Milk className="h-5 w-5" /> },
  { value: 'four_plus_times_a_week', label: '4+ times a week', icon: <Milk className="h-5 w-5" /> }
];

export const getAlcoholFrequencyOptions = (): AnimatedSelectionOption<FrequencyOption>[] => [
  { value: 'never', label: 'Never', icon: <GlassWater className="h-5 w-5" /> },
  { value: 'once_a_week', label: 'Once a week', icon: <Wine className="h-5 w-5" /> },
  { value: 'two_three_times_a_week', label: '2 - 3 times a week', icon: <Martini className="h-5 w-5" /> },
  { value: 'four_plus_times_a_week', label: '4+ times a week', icon: <Beer className="h-5 w-5" /> }
];

export const getExerciseFrequencyOptions = (): AnimatedSelectionOption<FrequencyOption>[] => [
  { value: 'never', label: 'Never', icon: <Armchair className="h-5 w-5" /> },
  { value: 'once_a_week', label: '1-2 times a week', icon: <Footprints className="h-5 w-5" /> },
  { value: 'two_three_times_a_week', label: '3-4 times a week', icon: <Volleyball className="h-5 w-5" /> },
  { value: 'four_plus_times_a_week', label: '5+ times a week', icon: <Dumbbell className="h-5 w-5" /> }
];

export const getStressLevelOptions = (): AnimatedSelectionOption<StressLevel>[] => [
  { value: 'very_low', label: 'Very Low', icon: <Laugh className="h-5 w-5" /> },
  { value: 'low', label: 'Low', icon: <Smile className="h-5 w-5" /> },
  { value: 'moderate', label: 'Moderate', icon: <Meh className="h-5 w-5" /> },
  { value: 'high', label: 'High', icon: <Frown className="h-5 w-5" /> }
];

export const EXERCISE_TYPES: { value: ExerciseType; label: string; icon: React.ReactNode; description: string }[] = [
  {
    value: 'cardio_endurance',
    label: 'Cardio & Endurance',
    icon: <Bike className="h-5 w-5" />,
    description: 'Things like running, cycling, swimming, other aerobic activities, or just staying active'
  },
  {
    value: 'strength',
    label: 'Strength Training',
    icon: <Dumbbell className="h-5 w-5" />,
    description: 'Things like  weight lifting, resistance training, or bodyweight exercises'
  },
  {
    value: 'mind_body_flexibility',
    label: 'Mind-Body & Flexibility',
    icon: <Smile className="h-5 w-5" />,
    description: 'Things like yoga, stretching, pilates, or mobility work'
  },
  {
    value: 'sports_recreational',
    label: 'Sports & Recreation',
    icon: <Volleyball className="h-5 w-5" />,
    description: 'Things like team sports, tennis, golf, or other recreational activities'
  },
  {
    value: 'general_fitness',
    label: 'General Fitness',
    icon: <Footprints className="h-5 w-5" />,
    description: 'Walking, light exercises, and staying active throughout the day'
  }
];

export interface HealthConcernCategory {
  category: string;
  items: { id: string; label: string }[];
}

export const HEALTH_CONCERNS: HealthConcernCategory[] = [
  {
    category: 'Mental Health & Cognitive',
    items: [
      { id: 'depression', label: 'Depression' },
      { id: 'anxiety', label: 'Anxiety' },
      { id: 'brain_fog', label: 'Brain Fog' },
      { id: 'adhd', label: 'ADHD' },
      { id: 'memory_issues', label: 'Memory Issues' },
      { id: 'mood_swings', label: 'Mood Swings' },
      { id: 'stress', label: 'Chronic Stress' },
      { id: 'insomnia', label: 'Insomnia' },
      { id: 'poor_sleep_quality', label: 'Poor Sleep Quality' }
    ]
  },
  {
    category: 'Cardiovascular',
    items: [
      { id: 'high_blood_pressure', label: 'High Blood Pressure' },
      { id: 'high_cholesterol', label: 'High Cholesterol' },
      { id: 'irregular_heartbeat', label: 'Irregular Heartbeat' },
      { id: 'poor_circulation', label: 'Poor Circulation' }
    ]
  },
  {
    category: 'Joint & Musculoskeletal',
    items: [
      { id: 'joint_pain', label: 'Joint Pain' },
      { id: 'arthritis', label: 'Arthritis' },
      { id: 'back_pain', label: 'Back Pain' },
      { id: 'muscle_soreness', label: 'Muscle Soreness' },
      { id: 'osteoporosis', label: 'Osteoporosis' },
      { id: 'tendonitis', label: 'Tendonitis' }
    ]
  },
  {
    category: 'Digestive',
    items: [
      { id: 'acid_reflux', label: 'Acid Reflux (GERD)' },
      { id: 'bloating', label: 'Bloating' },
      { id: 'constipation', label: 'Constipation' },
      { id: 'ibs', label: 'IBS' },
      { id: 'leaky_gut', label: 'Leaky Gut' },
      { id: 'food_sensitivities', label: 'Food Sensitivities' }
    ]
  },
  {
    category: 'Energy & Metabolism',
    items: [
      { id: 'chronic_fatigue', label: 'Chronic Fatigue' },
      { id: 'low_energy', label: 'Low Energy' },
      { id: 'diabetes', label: 'Diabetes' },
      { id: 'insulin_resistance', label: 'Insulin Resistance' },
      { id: 'thyroid_issues', label: 'Thyroid Issues' },
      { id: 'slow_metabolism', label: 'Slow Metabolism' }
    ]
  },
  {
    category: 'Immune System',
    items: [
      { id: 'frequent_illness', label: 'Frequent Illness' },
      { id: 'autoimmune_condition', label: 'Autoimmune Condition' },
      { id: 'allergies', label: 'Allergies' },
      { id: 'inflammation', label: 'Chronic Inflammation' }
    ]
  },
  {
    category: 'Skin, Hair & Nails',
    items: [
      { id: 'acne', label: 'Acne' },
      { id: 'eczema', label: 'Eczema' },
      { id: 'psoriasis', label: 'Psoriasis' },
      { id: 'dry_skin', label: 'Dry Skin' },
      { id: 'hair_loss', label: 'Hair Loss' },
      { id: 'brittle_nails', label: 'Brittle Nails' }
    ]
  },
  {
    category: 'Weight Management',
    items: [
      { id: 'overweight', label: 'Overweight' },
      { id: 'difficulty_losing_weight', label: 'Difficulty Losing Weight' },
      { id: 'difficulty_gaining_weight', label: 'Difficulty Gaining Weight' },
      { id: 'poor_appetite', label: 'Poor Appetite' },
      { id: 'emotional_eating', label: 'Emotional Eating' }
    ]
  },
  {
    category: 'Hormonal',
    items: [
      { id: 'low_testosterone', label: 'Low Testosterone' },
      { id: 'estrogen_imbalance', label: 'Estrogen Imbalance' },
      { id: 'pcos', label: 'PCOS' },
      { id: 'menopause_symptoms', label: 'Menopause Symptoms' },
      { id: 'pms', label: 'PMS' },
      { id: 'low_libido', label: 'Low Libido' }
    ]
  },
  {
    category: 'Vision & Eye Health',
    items: [
      { id: 'eye_strain', label: 'Eye Strain' },
      { id: 'dry_eyes', label: 'Dry Eyes' },
      { id: 'poor_night_vision', label: 'Poor Night Vision' },
      { id: 'macular_degeneration', label: 'Macular Degeneration' }
    ]
  },
  {
    category: 'Other',
    items: [
      { id: 'headaches', label: 'Headaches' },
      { id: 'migraines', label: 'Migraines' },
      { id: 'tinnitus', label: 'Tinnitus' },
      { id: 'dizziness', label: 'Dizziness' }
    ]
  }
];

// Interface for supplement categories
export interface SupplementCategory {
  category: string;
  items: string[];
}

// Common supplements list with categories
export const COMMON_SUPPLEMENTS: SupplementCategory[] = [
  {
    category: 'Essential Vitamins',
    items: [
      'Vitamin A',
      'Vitamin B Complex',
      'Vitamin B1 (Thiamine)',
      'Vitamin B2 (Riboflavin)',
      'Vitamin B3 (Niacin)',
      'Vitamin B5 (Pantothenic Acid)',
      'Vitamin B6',
      'Vitamin B7 (Biotin)',
      'Vitamin B9 (Folate)',
      'Vitamin B12',
      'Vitamin C',
      'Vitamin D3',
      'Vitamin E',
      'Vitamin K1',
      'Vitamin K2'
    ]
  },
  {
    category: 'Essential Minerals',
    items: [
      'Calcium',
      'Chromium',
      'Copper',
      'Iodine',
      'Iron',
      'Magnesium',
      'Manganese',
      'Potassium',
      'Selenium',
      'Zinc'
    ]
  },
  {
    category: 'Amino Acids & Proteins',
    items: [
      'L-Arginine',
      'L-Carnitine',
      'L-Citrulline',
      'L-Glutamine',
      'L-Theanine',
      'L-Tyrosine',
      'Taurine',
      'BCAAs',
      'EAAs',
      'Whey Protein',
      'Casein Protein',
      'Plant Protein',
      'Collagen Peptides'
    ]
  },
  {
    category: 'General Health Supplements',
    items: [
      'Fish Oil',
      'Omega-3',
      'Multivitamin',
      'CoQ10',
      'Alpha Lipoic Acid',
      'NAC (N-Acetyl Cysteine)',
      'Berberine',
      'Probiotics',
      'Digestive Enzymes',
      'Fiber Supplements',
      'Glucosamine',
      'MSM',
      'Chondroitin'
    ]
  },
  {
    category: 'Performance & Fitness',
    items: [
      'Creatine Monohydrate',
      'Beta-Alanine',
      'Caffeine',
      'Pre-Workout',
      'HMB',
      'Electrolytes',
      'Turkesterone',
      'Ecdysterone'
    ]
  },
  {
    category: 'Sleep & Recovery',
    items: [
      'Melatonin',
      'ZMA',
      'Magnesium Glycinate',
      'GABA',
      '5-HTP',
      'Glycine'
    ]
  },
  {
    category: 'Mental Health & Nootropics',
    items: [
      'Ashwagandha',
      'Rhodiola Rosea',
      'Lions Mane',
      'Alpha-GPC',
      'Bacopa Monnieri',
      'CDP-Choline',
      'Phosphatidylserine',
      'Ginkgo Biloba'
    ]
  },
  {
    category: 'Herbal & Natural',
    items: [
      'Turmeric/Curcumin',
      'Garlic Extract',
      'Green Tea Extract',
      'Milk Thistle',
      'Saw Palmetto',
      'Echinacea',
      'Elderberry',
      'Ginger Root',
      'St. Johns Wort'
    ]
  },
  {
    category: 'Common Medications',
    items: [
      'Metformin',
      'Ozempic',
      'Finasteride',
      'Dutasteride',
      'Minoxidil',
      'Cialis',
      'Viagra',
      'Modafinil',
      'Adderall',
      'SSRIs',
      'Beta Blockers',
      'Birth Control',
      'TRT'
    ]
  },
  {
    category: 'Research Compounds & Peptides',
    items: [
      'BPC-157',
      'TB-500',
      'MK-677',
      'GW-501516',
      'SR-9009',
      'RAD-140',
      'LGD-4033',
      'YK-11',
      'S-23',
      'S-4'
    ]
  },
  {
    category: 'Performance Enhancement',
    items: [
      'Testosterone',
      'Trenbolone',
      'Deca-Durabolin',
      'Dianabol',
      'Anavar',
      'Winstrol',
      'Anadrol',
      'HGH',
      'Clenbuterol',
      'HCG',
      'Arimidex',
      'Nolvadex'
    ]
  }
];

export interface DietaryRequirementCategory {
  category: string;
  items: string[];
}

export const DIETARY_REQUIREMENTS: DietaryRequirementCategory[] = [
  {
    category: "Common Dietary Requirements",
    items: [
      "Vegetarian",
      "Vegan",
      "Pescatarian",
      "Gluten-Free",
      "Dairy-Free",
      "Nut-Free",
      "Halal",
      "Kosher",
      "Low-Carb",
      "Low-Fat",
      "Keto",
      "Paleo",
      "Mediterranean",
      "Low-Sodium",
      "Low-FODMAP"
    ]
  },
  {
    category: "Common Allergens",
    items: [
      "Peanuts",
      "Tree Nuts",
      "Milk",
      "Eggs",
      "Soy",
      "Wheat",
      "Fish",
      "Shellfish",
      "Sesame",
      "Mustard",
      "Celery",
      "Lupin",
      "Molluscs"
    ]
  }
]; 